import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  useAcquireVariantLockMutation,
  useRefreshVariantLockMutation,
  useReleaseVariantLockMutation,
} from '../../api/variantApi';
import { selectUser } from '../../authentication/redux/selectors';
import {
  selectIsUserInstituteAllowed,
  selectVariantLock,
} from '../redux/selectors';

/**
 * Global Tracking for Variant Locks
 *
 * Since useVariantLock can be used in multiple components/tabs for the same variant,
 * we need to track how many instances are currently "holding" the lock.
 * Only the first instance triggers the backend 'acquire' and the last one triggers 'release'.
 */
const lockHolders: Record<string | number, number> = {};
const releaseTimers: Record<string | number, number> = {};

/**
 * Variant Lock Hook - Manages automatic variant locking
 *
 * Mutation hooks from RTK Query replace the old `dispatch(thunk(...)).unwrap()` pattern.
 * The slice state is updated automatically via matchFulfilled/matchRejected matchers in
 * slice.ts (see RTK Query tutorial Part 8: "Updating Toast Display").
 *
 * @param variantId - The ID of the variant to lock
 */
export const useVariantLock = (variantId?: number | string) => {
  const user = useSelector(selectUser);
  const lock = useSelector(selectVariantLock);
  const currentUser = user?.username;

  const isUserInstituteAllowed = useSelector(selectIsUserInstituteAllowed);

  // RTK Query mutation hooks – trigger functions can be called inside effects
  const [acquireVariantLockMutation] = useAcquireVariantLockMutation();
  const [refreshVariantLockMutation] = useRefreshVariantLockMutation();
  const [releaseVariantLockMutation] = useReleaseVariantLockMutation();

  const timerRef = useRef<number | null>(null);
  const isAcquiringLock = useRef<boolean>(false);
  const hasAcquiredLock = useRef<boolean>(false);
  const latestLockRef = useRef(lock);

  useEffect(() => {
    latestLockRef.current = lock;
  }, [lock]);

  useEffect(() => {
    if (!variantId || !currentUser || !isUserInstituteAllowed) {
      return;
    }
    if (latestLockRef.current?.lock_type === 'refresh') {
      return;
    }

    // --- 1. GLOBAL TRACKING & BUFFER (500ms) ---
    lockHolders[variantId] = (lockHolders[variantId] || 0) + 1;

    if (releaseTimers[variantId]) {
      window.clearTimeout(releaseTimers[variantId]);
      delete releaseTimers[variantId];
    }

    // --- 2. LOCK ACQUISITION LOGIC ---
    const performAcquireLock = async () => {
      if (isAcquiringLock.current || hasAcquiredLock.current) {
        return;
      }
      try {
        isAcquiringLock.current = true;
        // Calling the RTK Query mutation trigger – same as tutorial's
        // `await addNewPost({...}).unwrap()` pattern (Part 7).
        const result = await acquireVariantLockMutation({
          variant_id: variantId,
        }).unwrap();

        if (result?.locked_by === currentUser) {
          hasAcquiredLock.current = true;
        }
      } catch (error: any) {
        // error is the transformErrorResponse result: { locked_by, message }
        // (see acquireVariantLock endpoint in variantApi.ts)
        if (error?.locked_by && error?.locked_by !== currentUser) {
          hasAcquiredLock.current = false;
        }
      } finally {
        isAcquiringLock.current = false;
      }
    };

    if (!hasAcquiredLock.current) {
      performAcquireLock();
    }

    // --- 3. HEARTBEAT (30s) ---
    timerRef.current = window.setInterval(async () => {
      if (latestLockRef.current?.lock_type === 'refresh') {
        return;
      }
      const currentLockOwner = latestLockRef.current?.locked_by;

      if (!currentLockOwner || currentLockOwner === currentUser) {
        try {
          await refreshVariantLockMutation({ variant_id: variantId }).unwrap();
        } catch (error: any) {
          const status = error?.status;
          if (status === 404) {
            // SELF-HEALING: lock was lost, try to re-acquire
            hasAcquiredLock.current = false;
            performAcquireLock();
          } else if (status === 403 && timerRef.current) {
            // STOP: another user took the lock
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }
    }, 30 * 1000);

    // --- 4. CLEANUP ---
    const handleBeforeUnload = () => {
      if (
        latestLockRef.current?.lock_type !== 'refresh' &&
        latestLockRef.current?.locked_by === currentUser &&
        lockHolders[variantId] === 1
      ) {
        releaseVariantLockMutation({ variant_id: variantId });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      lockHolders[variantId] -= 1;

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);

      hasAcquiredLock.current = false;
      isAcquiringLock.current = false;

      // ONLY RELEASE AFTER 500ms (to survive Navigation/Refresh/Remount)
      if (
        latestLockRef.current?.lock_type !== 'refresh' &&
        latestLockRef.current?.locked_by === currentUser &&
        lockHolders[variantId] === 0
      ) {
        releaseTimers[variantId] = window.setTimeout(() => {
          releaseVariantLockMutation({ variant_id: variantId });
          delete releaseTimers[variantId];
        }, 500);
      }
    };
  }, [
    variantId,
    currentUser,
    isUserInstituteAllowed,
    acquireVariantLockMutation,
    refreshVariantLockMutation,
    releaseVariantLockMutation,
    lock?.lock_type,
  ]);
};
